import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useService } from '@hooks';
import { Tr } from '@utils';
import { carListBandService, simulationService, SimulationTask, SimulationTaskStatus } from '@services';
import { Avatar, Badge, Box, Button, Stack, Typography } from '@mui/material';
import { PortalPageTitle } from '@components/portal/common';
import { VehicleSuggestionList, VehicleSuggestionListCarBandListFilter } from '@components/portal/dashboard';
import { SettingsIcon } from '@components/icons';

const paddingX = 9;

export const VehicleSuggestions: FC = () => {
	const navigate = useNavigate();
	const [filterPanelOpen, setFilterPanelOpen] = useState(false);
	const [selectedCarListBands, setSelectedCarListBands] = useState<string[]>([]);
	const [filteredSimulationTasks, setFilteredSimulationTasks] = useState<SimulationTask[]>([]);
	const { data: simulationTasks } = useService(() => simulationService.getLatestSimulationTasks());
	const { data: carListBands = [] } = useService(() => carListBandService.getAllFromCache());
	useEffect(() => {
		let result = (simulationTasks || []).filter((task) => task.status === SimulationTaskStatus.FULFILLED);
		if (selectedCarListBands?.length) {
			result = result.filter((v) => v?.fleetVehicle?.segmentId && selectedCarListBands.includes(v.fleetVehicle.segmentId));
		}
		setFilteredSimulationTasks(result);
	}, [simulationTasks, selectedCarListBands]);

	return (
		<Stack direction="column" sx={{ height: 1.0, maxHeight: 1.0, paddingY: 5, overflow: 'hidden' }}>
			<PortalPageTitle
				paddingX={3}
				sx={{ paddingRight: paddingX }}
				title={<Tr.Portal path="dashboard.vehicle-suggestions.title" />}
				subtitle={<Tr.Portal path="dashboard.vehicle-suggestions.subtitle" />}
				onBackButtonClick={() => navigate(-1)}
			>
				{carListBands?.length ? (
					<Stack
						direction="row"
						alignItems="center"
						spacing={1}
						sx={{ cursor: 'pointer', userSelect: 'none' }}
						onClick={() => setFilterPanelOpen(!filterPanelOpen)}
					>
						<Typography>
							<Tr.Portal path="dashboard.vehicle-suggestions.filter-title" />
						</Typography>
						<Badge
							invisible={!selectedCarListBands.length}
							badgeContent={
								<Box
									sx={{
										backgroundColor: 'common.black',
										width: 12,
										height: 12,
										borderStyle: 'solid',
										borderWidth: 2,
										borderColor: 'common.white',
										borderRadius: '50%'
									}}
								/>
							}
							overlap="rectangular"
							sx={{
								'& .MuiBadge-badge': {
									top: 4,
									right: 4
								}
							}}
						>
							<Avatar sx={{ backgroundColor: filterPanelOpen ? 'primary.main' : 'common.black', width: 40, height: 40 }}>
								<SettingsIcon />
							</Avatar>
						</Badge>
					</Stack>
				) : null}
			</PortalPageTitle>
			{filterPanelOpen ? (
				<Stack sx={{ paddingX: 5, marginTop: 3 }}>
					<VehicleSuggestionListCarBandListFilter
						carListBands={carListBands}
						selectedCarListBands={selectedCarListBands}
						onSelectChange={setSelectedCarListBands}
					/>
				</Stack>
			) : null}
			{selectedCarListBands?.length ? (
				<Stack
					direction="row"
					justifyContent="space-between"
					alignItems="center"
					sx={{ paddingLeft: paddingX, paddingRight: paddingX - 2, marginTop: 2 }}
				>
					<Typography variant="h5">
						<Tr.Portal
							path={filteredSimulationTasks.length > 1 ? 'results' : 'result'}
							options={{ length: filteredSimulationTasks.length }}
						/>
					</Typography>
					<Button
						data-testid="dashboard-vehicle-suggesiton-list-clear-filter-button"
						variant="text"
						size="small"
						sx={{ padding: 2, marginLeft: 'auto' }}
						onClick={() => (setSelectedCarListBands([]), setFilterPanelOpen(false))}
					>
						<Tr.Portal path="clear-filter" />
					</Button>
				</Stack>
			) : null}
			{filteredSimulationTasks.length ? (
				<Stack
					direction="column"
					flexGrow={1}
					sx={{ overflowY: 'auto', paddingTop: !filterPanelOpen && !selectedCarListBands.length ? 3 : 1 }}
				>
					<VehicleSuggestionList paddingX={paddingX} entities={filteredSimulationTasks} />
				</Stack>
			) : null}
		</Stack>
	);
};
